import { A11yModule } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	HostBinding,
	HostListener,
	Inject,
	Input,
	Renderer2,
	ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { fadeAnimation_times } from '@siza-staff-care/shared/ui-components/global-animations';
import { ORIGIN_REF, PANEL_CONFIG, PanelConfig, PanelRef } from './popup-panel.service';

const animation = fadeAnimation_times(200);

@Component({
	selector: 'gd-popup-panel',
	standalone: true,
	animations: [animation],
	imports: [CommonModule, MatButtonModule, MatIconModule, A11yModule],
	// eslint-disable-next-line @angular-eslint/no-host-metadata-property
	host: {
		class: 'popup-panel',
	},
	template: `
		<div class="wrapper" cdkTrapFocus cdkTrapFocusAutoCapture [class.no-padding]="noPadding">
			@if (!noTitle) {
				<div class="header">
					<h1>{{ titleText }}</h1>
					<div class="toolbar-content">
						<ng-content select="[slot=toolbar]"></ng-content>
					</div>
				</div>
			}
			@if (!hideClose) {
				<button class="tiny-button" (click)="panelRef.close()" mat-icon-button>
					<mat-icon>close</mat-icon>
				</button>
			}

			<div class="content">
				<ng-content />
			</div>
		</div>

		@if (showPointer) {
			<div class="button-pointer" #pointer></div>
		}
	`,
	styles: [
		`
			:host {
				display: block;

				$triangle-size: 10px;
				$triangle-offset: $triangle-size - 1px;

				position: relative;
				min-height: 40px;

				button {
					margin-bottom: 0;
					position: absolute;
					z-index: 10;
					right: 4px;
					top: 4px;
				}

				.button-pointer {
					position: absolute;
					top: 0;

					border-left: $triangle-size solid transparent;
					border-right: $triangle-size solid transparent;

					border-bottom: $triangle-size solid #fff;

					transform: translateY(-$triangle-offset);
				}

				.header {
					width: 100%;
					display: flex;
					align-items: center;
					padding-bottom: 2px;

					h1 {
						font-size: 1.25rem;
						margin: 0;
						margin-right: 16px;
					}

					.toolbar-content {
						flex: 1;
					}
				}
			}
		`,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PopupPanelComponent implements AfterViewInit {
	@ViewChild('pointer') pointer?: ElementRef;

	@HostBinding('@fadeAnimation') fadeAnimation = true;

	@Input() titleText?: string;
	@Input() pointerLeft = 0;
	@Input()
	get closeOnEscape(): boolean {
		return this._closeOnEscape;
	}
	set closeOnEscape(value: BooleanInput) {
		this._closeOnEscape = coerceBooleanProperty(value);
	}
	private _closeOnEscape = false;
	@Input()
	get showPointer(): boolean {
		return this._showPointer;
	}
	set showPointer(value: BooleanInput) {
		this._showPointer = coerceBooleanProperty(value);
	}
	private _showPointer = false;

	@Input()
	get hideClose(): boolean {
		return this._hideClose;
	}
	set hideClose(value: BooleanInput) {
		this._hideClose = coerceBooleanProperty(value);
	}
	private _hideClose = false;

	@Input()
	get noTitle(): boolean {
		return this._noTitle;
	}
	set noTitle(value: BooleanInput) {
		this._noTitle = coerceBooleanProperty(value);
	}
	private _noTitle = false;

	@HostBinding('class.no-padding')
	@Input()
	get noPadding(): boolean {
		return this._noPadding;
	}
	set noPadding(value: BooleanInput) {
		this._noPadding = coerceBooleanProperty(value);
	}
	private _noPadding = false;

	@HostListener('document:keydown.Escape')
	handleKeydown() {
		if (!this.closeOnEscape) {
			return;
		}

		this.close();
	}

	constructor(
		protected panelRef: PanelRef,
		@Inject(PANEL_CONFIG) protected config: PanelConfig,
		@Inject(ORIGIN_REF) private originRef: ElementRef,
		private thisElementRef: ElementRef,
		private renderer: Renderer2,
	) {}

	ngAfterViewInit(): void {
		if (!this.originRef || !this.pointer) {
			return;
		}

		setTimeout(() => {
			if (!this.pointer) {
				return;
			}

			const originRect = this.originRef.nativeElement.getBoundingClientRect();
			const thisRect = this.thisElementRef.nativeElement.getBoundingClientRect();

			const thisLeft = thisRect.left + thisRect.width;
			const originLeft = originRect.left + originRect.width;

			if (Math.abs(thisLeft - originLeft) < 10) {
				// We're positioned near the left of the origin
				const rightOffset = originRect.width / 2;
				this.renderer.setStyle(this.pointer.nativeElement, 'right', rightOffset - 7 + 'px');
			} else {
				// We're positioned near the right of the origin
				const leftOffset = originRect.width / 2;
				this.renderer.setStyle(this.pointer.nativeElement, 'left', leftOffset - 7 + 'px');
			}
		});
	}

	close() {
		this.panelRef.close();
	}
}
